import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { dataProviders } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { memo } from 'react';

import { PLACEHOLDER_FIELD } from '../../RequestSearchPopup.constants';
import { DateFieldRow, DateRow, WrapFieldRow } from '../../RequestSearchPopup.styles';
import { NetSuiteCustomSearchFields } from '../NetSuiteCustomSearchFields/NetSuiteCustomSearchFields';
import { messages } from './NetSuiteSearchFields.messages';
import { NetSuiteSearchFieldsProps } from './NetSuiteSearchFields.types';

export const NetSuiteSearchFields = memo<NetSuiteSearchFieldsProps>((props) => {
    const { company, integrationCode, integrationEnabled, params, updateParameter, disabled } = props;

    if (!integrationCode) {
        return null;
    }

    const isBill = integrationCode === domain.IntegrationCode.NetSuiteBill;
    const isPO = integrationCode === domain.IntegrationCode.NetSuitePO;
    const isExpenseReport = integrationCode === domain.IntegrationCode.NetSuiteExpenseReport;
    const isVRA = integrationCode === domain.IntegrationCode.NetSuiteVRA;
    const isBillPayment = integrationCode === domain.IntegrationCode.NetSuiteBillPayment;
    const isSalesOrder = integrationCode === domain.IntegrationCode.NetSuiteSalesOrder;
    const isInvoice = integrationCode === domain.IntegrationCode.NetSuiteInvoice;
    const isVendor = integrationCode === domain.IntegrationCode.NetSuiteVendor;
    const isJournalEntry = integrationCode === domain.IntegrationCode.NetSuiteJournalEntry;
    const isItemReceipt = integrationCode === domain.IntegrationCode.NetSuiteItemReceipt;

    return (
        <>
            <WrapFieldRow>
                <Field title={messages.requestorFieldTitle}>
                    <PlainDataProvider items={company?.allMembers || []} filterAttribute='displayName'>
                        <DropdownEditor
                            displayAttribute='displayName'
                            placeholder={messages.requestorPlaceholder}
                            value={params.requestor}
                            onChange={updateParameter('requestor')}
                            disabled={disabled}
                        />
                    </PlainDataProvider>
                </Field>

                {(isBill || isInvoice || isJournalEntry || isItemReceipt) && (
                    <Field title={messages.netSuiteReferenceFieldTitle}>
                        <TextEditor
                            placeholder={messages.netSuiteReferenceFieldPlaceholder}
                            value={params.netSuiteReference}
                            onChange={updateParameter('netSuiteReference')}
                            disabled={disabled}
                        />
                    </Field>
                )}

                {(isBill || isVRA || isBillPayment || isInvoice || isJournalEntry) && (
                    <Field title={messages.netSuiteTransactionNumberFieldTitle}>
                        <TextEditor
                            placeholder={messages.netSuiteTransactionNumberFieldPlaceholder}
                            value={params.netSuiteTransactionNumber}
                            onChange={updateParameter('netSuiteTransactionNumber')}
                            disabled={disabled}
                        />
                    </Field>
                )}

                {(isBill || isVRA || isVendor || isItemReceipt) && (
                    <Field title={messages.netSuiteVendorFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteVendor
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteVendorFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteVendor}
                                onChange={updateParameter('netSuiteVendor')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {isBillPayment && (
                    <Field title={messages.netSuitePayeeFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuitePayee
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuitePayeeFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuitePayee}
                                onChange={updateParameter('netSuitePayee')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {isPO && (
                    <>
                        <Field title={messages.netSuitePOVendorReferenceTitle}>
                            <TextEditor
                                placeholder={messages.netSuitePOVendorReferencePlaceholder}
                                value={params.netSuitePOVendorReference}
                                onChange={updateParameter('netSuitePOVendorReference')}
                                disabled={disabled}
                            />
                        </Field>

                        <Field title={messages.netSuitePOReferenceTitle}>
                            <TextEditor
                                placeholder={messages.netSuitePOReferencePlaceholder}
                                value={params.netSuitePOReference}
                                onChange={updateParameter('netSuitePOReference')}
                                disabled={disabled}
                            />
                        </Field>

                        <Field title={messages.netSuiteVendorFieldTitle}>
                            <dataProviders.FieldDataProvider
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteVendor
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.netSuiteVendorFieldPlaceholder}
                                    disabled={disabled || !integrationEnabled}
                                    value={params.netSuiteVendor}
                                    onChange={updateParameter('netSuiteVendor')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    </>
                )}

                {isExpenseReport && (
                    <>
                        <Field title={messages.netSuiteTransactionNumberFieldTitle}>
                            <TextEditor
                                placeholder={messages.netSuiteTransactionNumberFieldPlaceholder}
                                value={params.netSuiteTransactionNumber}
                                onChange={updateParameter('netSuiteTransactionNumber')}
                                disabled={disabled}
                            />
                        </Field>

                        <Field title={messages.netSuiteEmployeeFieldTitle}>
                            <dataProviders.FieldDataProvider
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteEmployee
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.netSuiteEmployeeFieldPlaceholder}
                                    disabled={disabled || !integrationEnabled}
                                    value={params.netSuiteEmployee}
                                    onChange={updateParameter('netSuiteEmployee')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    </>
                )}

                {isSalesOrder && (
                    <>
                        <Field title={messages.netSuiteReferenceFieldTitle}>
                            <TextEditor
                                placeholder={messages.netSuiteReferenceFieldPlaceholder}
                                value={params.netSuiteReference}
                                onChange={updateParameter('netSuiteReference')}
                                disabled={disabled}
                            />
                        </Field>

                        <Field title={messages.netSuiteTransactionNumberFieldTitle}>
                            <TextEditor
                                placeholder={messages.netSuiteTransactionNumberFieldPlaceholder}
                                value={params.netSuiteTransactionNumber}
                                onChange={updateParameter('netSuiteTransactionNumber')}
                                disabled={disabled}
                            />
                        </Field>

                        <Field title={messages.netSuiteCustomerFieldTitle}>
                            <dataProviders.FieldDataProvider
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteCustomer
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.netSuiteCustomerFieldPlaceholder}
                                    disabled={disabled || !integrationEnabled}
                                    value={params.netSuiteCustomer}
                                    onChange={updateParameter('netSuiteCustomer')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    </>
                )}

                {isBillPayment && (
                    <>
                        <Field title={messages.netSuiteDepartmentFieldTitle}>
                            <dataProviders.FieldDataProvider
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteDepartment
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.netSuiteDepartmentFieldPlaceholder}
                                    disabled={disabled || !integrationEnabled}
                                    value={params.netSuiteDepartment}
                                    onChange={updateParameter('netSuiteDepartment')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>

                        <Field title={messages.netSuiteClassFieldTitle}>
                            <dataProviders.FieldDataProvider
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteClass
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.netSuiteClassFieldPlaceholder}
                                    disabled={disabled || !integrationEnabled}
                                    value={params.netSuiteClass}
                                    onChange={updateParameter('netSuiteClass')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>

                        <Field title={messages.netSuiteLocationFieldTitle}>
                            <dataProviders.FieldDataProvider
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteLocation
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.netSuiteLocationFieldPlaceholder}
                                    disabled={disabled || !integrationEnabled}
                                    value={params.netSuiteLocation}
                                    onChange={updateParameter('netSuiteLocation')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    </>
                )}

                {isInvoice && (
                    <Field title={messages.netSuiteCustomerFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (field) => field.systemPurpose === domain.FieldSystemPurpose.NetSuiteCustomer
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.netSuiteCustomerFieldPlaceholder}
                                disabled={disabled || !integrationEnabled}
                                value={params.netSuiteCustomer}
                                onChange={updateParameter('netSuiteCustomer')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                <NetSuiteCustomSearchFields
                    params={params}
                    integrationCode={integrationCode}
                    integrationEnabled={integrationEnabled}
                    disabled={disabled}
                    isLineCustomFields={false}
                    updateParameter={updateParameter}
                />
            </WrapFieldRow>

            {(isBill || isPO || isExpenseReport) && (
                <DateRow>
                    <Field title={messages.netSuiteDueDateFieldTitle}>
                        <DateFieldRow>
                            <div>{messages.dateFromLabel}</div>

                            <DateTimeEditor
                                placeholder={messages.dateGreaterEqualsPlaceholder}
                                value={params.netSuiteDueDateGreaterEquals}
                                onChange={updateParameter('netSuiteDueDateGreaterEquals')}
                                disabled={disabled}
                            />

                            <div>{messages.dateToLabel}</div>

                            <DateTimeEditor
                                placeholder={messages.dateLessEqualsPlaceholder}
                                value={params.netSuiteDueDateLessEquals}
                                onChange={updateParameter('netSuiteDueDateLessEquals')}
                                disabled={disabled}
                            />
                        </DateFieldRow>
                    </Field>
                </DateRow>
            )}

            {!isBillPayment && !isVendor && (
                <DateRow>
                    <Field title={messages.netSuiteDateFieldTitle}>
                        <DateFieldRow>
                            <div>{messages.dateFromLabel}</div>

                            <DateTimeEditor
                                placeholder={messages.dateGreaterEqualsPlaceholder}
                                value={params.netSuiteDateGreaterEquals}
                                onChange={updateParameter('netSuiteDateGreaterEquals')}
                                disabled={disabled}
                            />

                            <div>{messages.dateToLabel}</div>

                            <DateTimeEditor
                                placeholder={messages.dateLessEqualsPlaceholder}
                                value={params.netSuiteDateLessEquals}
                                onChange={updateParameter('netSuiteDateLessEquals')}
                                disabled={disabled}
                            />
                        </DateFieldRow>
                    </Field>
                </DateRow>
            )}
        </>
    );
});

NetSuiteSearchFields.displayName = 'NetSuiteSearchFields';
